import { GetStaticProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { groq } from 'next-sanity';
import { LiveQuery } from 'next-sanity/preview/live-query';
import { ReactElement } from 'react';
import { CSSProperties } from 'react';

import { LinkButtonRound } from '@components/Buttons/LinkButton';
import { StandardLayout } from '@components/Layouts';
import { Paragraph } from '@components/Typography';
import { PortableTextBlockRenderer } from '@components/Typography/PortableText';
import { shimmer, toBase64, urlFor } from '@lib/images';
import { sanityFetch } from 'lib/sanity.fetch';

import type { PortableTextBlock } from '@portabletext/types';

const query = groq`*[_id == "b850e859-2a6b-439a-b429-50730f4f3fca"]
  {
    title,
    metaTitle,
    metaDescription,
    lawyers[]->{
      name,
      jobTitle,
      pronouns,
      summary,
      image,
      content
    },
  }[0]`;

export interface PageData {
  title: string;
  metaTitle: string;
  metaDescription: string;
  lawyers: Array<{
    name: string;
    jobTitle: string;
    pronouns: string;
    summary: string;
    content: PortableTextBlock | PortableTextBlock[];
    image: {
      src: string;
      alt: string;
      blur: string;
    };
  }>;
}

// eslint-disable-next-line no-restricted-syntax
export default function About({
  draftMode,
  pageData,
}: {
  draftMode: boolean;
  pageData: PageData;
}) {
  const imageStyles: CSSProperties = {
    objectFit: 'contain',
    filter: 'grayscale(100%)',
  };

  console.log(pageData);

  return (
    <LiveQuery enabled={draftMode} query={query} initialData={pageData}>
      <Head>
        <title key="title">{pageData.metaTitle}</title>
        <meta name="title" content={pageData.metaTitle} key="metatitle" />
        <meta
          name="description"
          content={pageData.metaDescription}
          key="metadescription"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/about`}
        />

        <meta
          property="og:title"
          content="Expert criminal legal advice in Tasmania"
          key="ogtitle"
        />
        <meta
          property="og:url"
          content="https://www.monklawyers.com.au/about"
          key="ogurl"
        />
        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_SERVER_URL}/images/Footer logo-12.png`}
          key="ogimage"
        />
        <meta property="og:type" content="website" key="ogtype" />
        <meta
          property="og:description"
          content="Monk Lawyers is a premier criminal defence law firm led by Pip Monk. Get expert advice and representation in Court in Tasmania. We fight for your rights."
          key="ogdescription"
        />
      </Head>

      <main className="bg-white">
        <h1 className="bg-blue pt-[105px] pl-[155px]">{pageData.title}</h1>
        {pageData.lawyers.map((lawyer, idx) => (
          <section key={lawyer.name}>
            <div
              key={lawyer.name}
              className="text-blue relative m-0 grid grid-cols-1 py-12 md:mx-10 md:grid-cols-[auto_auto] xl:py-[105px]"
            >
              {lawyer.image && (
                <figure className="tiny:w-[400px] relative mx-auto my-0 h-fit min-h-[450px] w-[320px]">
                  <Image
                    src={lawyer.image.src}
                    fill
                    sizes="(max-width: 400px) 320px, 400px"
                    alt={lawyer.image.alt}
                    placeholder="blur"
                    blurDataURL={lawyer.image.blur}
                    quality={100}
                    style={imageStyles}
                  />
                </figure>
              )}

              <div>
                <div className="min-h-[300px] px-5 md:min-h-[620px] xl:px-[135px]">
                  <h2>
                    {lawyer.name} - {lawyer.jobTitle}
                  </h2>
                  <p className="font-secondary text-xs">{lawyer.pronouns}</p>

                  <Paragraph className="italic">{lawyer.summary}</Paragraph>
                  <PortableTextBlockRenderer content={lawyer.content} />
                </div>
              </div>
            </div>
            {pageData.lawyers[idx + 1] && <hr className="pb-12.5 md:hidden" />}
          </section>
        ))}
      </main>
    </LiveQuery>
  );
}

About.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async ({ draftMode = false }) => {
  const data = await sanityFetch<PageData>({ draftMode, query });

  const blur = `data:image/svg+xml;base64,${toBase64(shimmer(2000, 1200))}`;
  data.lawyers.forEach((lawyer) => {
    if (!lawyer.image) return;
    const src = urlFor(lawyer.image).width(400).quality(85).url();
    lawyer.image = { src, alt: lawyer.image.alt, blur };
  });

  const pageData: PageData = {
    ...data,
  };

  return {
    props: {
      draftMode,
      pageData,
    },
  };
};
